import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded'
import CameraswitchRoundedIcon from '@mui/icons-material/CameraswitchRounded'
import { ScanDeduper, looksLikeBadgeToken } from '../scanDedupe'

/**
 * Live camera QR scanner for check-in badges.
 *
 * Uses @zxing/browser (lazy-imported so it only loads when the scanner opens).
 * Continuously decodes from the rear camera and calls `onDecode(token)` for each
 * *new* badge code (deduped over a short window so one badge held up doesn't fire
 * repeat check-ins). The parent owns the check-in call + result feedback; this
 * component just turns camera frames into tokens.
 *
 * Robust lifecycle: the camera stream is always stopped on unmount / when
 * `paused` flips true, so there's no lingering camera light. Handles
 * permission-denied, no-camera, and insecure-context with a clear message and a
 * manual-entry fallback (rendered by the parent).
 */
export default function BadgeScanner({
  onDecode,
  paused = false,
  dedupeMs = 3000,
  height = 320,
}: {
  onDecode: (token: string) => void
  /** When true, scanning is suspended (e.g. while showing a result). Camera stays warm. */
  paused?: boolean
  dedupeMs?: number
  height?: number
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // zxing "controls" object with a .stop(); typed loosely to avoid importing types eagerly.
  const controlsRef = useRef<{ stop: () => void } | null>(null)
  const deduperRef = useRef(new ScanDeduper(dedupeMs))
  const pausedRef = useRef(paused)
  const onDecodeRef = useRef(onDecode)

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [deviceIndex, setDeviceIndex] = useState(0)
  const [status, setStatus] = useState<'idle' | 'starting' | 'running'>('idle')
  const [error, setError] = useState<string | null>(null)

  // Keep refs current without restarting the camera.
  useEffect(() => {
    pausedRef.current = paused
  }, [paused])
  useEffect(() => {
    onDecodeRef.current = onDecode
  }, [onDecode])

  const stop = useCallback(() => {
    try {
      controlsRef.current?.stop()
    } catch {
      /* ignore */
    }
    controlsRef.current = null
    // Belt-and-braces: stop any tracks still attached to the element.
    const stream = videoRef.current?.srcObject as MediaStream | null
    stream?.getTracks().forEach((t) => t.stop())
    if (videoRef.current) videoRef.current.srcObject = null
    setStatus('idle')
  }, [])

  const start = useCallback(async () => {
    setError(null)
    if (!window.isSecureContext) {
      setError(
        'Camera access needs a secure (HTTPS) connection. Use the deployed site, or enter the code manually below.',
      )
      return
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser does not support camera access. Enter the code manually below.')
      return
    }
    setStatus('starting')
    try {
      const { BrowserQRCodeReader } = await import('@zxing/browser')
      const reader = new BrowserQRCodeReader()

      // Enumerate cameras (best-effort; requires a prior permission grant on
      // some browsers, but decodeFromVideoDevice will still prompt).
      let cams: MediaDeviceInfo[] = []
      try {
        cams = await BrowserQRCodeReader.listVideoInputDevices()
        setDevices(cams)
      } catch {
        /* enumeration can fail before permission; continue with default */
      }

      // Prefer a rear/environment camera when we can identify one.
      let deviceId: string | undefined
      if (cams.length) {
        const rear = cams.find((d) => /back|rear|environment/i.test(d.label))
        const chosen = cams[deviceIndex] ?? rear ?? cams[0]
        deviceId = chosen?.deviceId
      }

      const controls = await reader.decodeFromVideoDevice(
        deviceId,
        videoRef.current as HTMLVideoElement,
        (result) => {
          if (!result || pausedRef.current) return
          const text = result.getText()
          // Ignore QR codes that aren't our badge format.
          if (!looksLikeBadgeToken(text)) return
          if (deduperRef.current.accept(text)) {
            onDecodeRef.current(text.trim())
          }
        },
      )
      controlsRef.current = controls
      setStatus('running')
    } catch (e) {
      const name = (e as { name?: string })?.name
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setError('Camera permission was denied. Allow camera access, or enter the code manually below.')
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setError('No camera found on this device. Enter the code manually below.')
      } else {
        setError('Could not start the camera. Enter the code manually below.')
      }
      setStatus('idle')
    }
  }, [deviceIndex])

  // Start on mount; stop on unmount.
  useEffect(() => {
    start()
    return () => stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const switchCamera = () => {
    if (devices.length < 2) return
    stop()
    setDeviceIndex((i) => (i + 1) % devices.length)
    // Restart with the new index on next tick.
    setTimeout(() => start(), 50)
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#000',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          muted
          playsInline
        />
        {/* Scan reticle overlay */}
        {status === 'running' && !error && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: '60%',
                maxWidth: 240,
                aspectRatio: '1 / 1',
                border: '3px solid',
                borderColor: paused ? 'warning.main' : 'primary.main',
                borderRadius: 3,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                transition: 'border-color .2s',
              }}
            />
          </Box>
        )}
        {status !== 'running' && !error && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <CameraAltRoundedIcon />
              <Typography variant="body2">
                {status === 'starting' ? 'Starting camera…' : 'Camera off'}
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {error
            ? ' '
            : paused
              ? 'Hold for the result…'
              : 'Point the camera at an attendee’s badge QR code.'}
        </Typography>
        {devices.length > 1 && (
          <Button
            size="small"
            startIcon={<CameraswitchRoundedIcon />}
            onClick={switchCamera}
          >
            Switch camera
          </Button>
        )}
      </Stack>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}
